/**
 * doc/07 PR-45 — first-run onboarding overlay.
 *
 * Shows once, for a brand-new user (their active project has no cards
 * yet) who hasn't dismissed it before. Orients them on the core loop
 * and offers 3 one-click starter prompts so the very first thing they
 * do produces the "talk → cards appear" aha moment.
 *
 * Visibility is self-managed: renders null unless `eligible` (parent's
 * "this is a new/empty project" signal) AND not previously dismissed
 * (localStorage). Dismissal persists so it never nags a returning user.
 *
 * Coupling is deliberately thin: parent passes `eligible`, an
 * `onTryPrompt(text)` (wired to the normal send path), and `onClose`.
 * If the parent can't send right now (no active thread), onTryPrompt
 * can no-op — the overlay still closes and the user types on their own.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const DISMISS_KEY = 'draftlize:onboarded';

interface Props {
  /** Parent signal: this is a fresh/empty project worth onboarding. */
  eligible: boolean;
  /** Send a starter prompt through the normal chat path. */
  onTryPrompt: (text: string) => void;
  /** Called when the overlay is dismissed (parent may refocus composer). */
  onClose: () => void;
}

/** Starter prompts resolve through i18n (doc/12 §3.4: the friends-circle
 *  launch audience is zh-first — a hardcoded English overlay was the
 *  single worst first impression on the aha path). Keys under
 *  onboarding.starter{1,2,3}{Label,Prompt}. */
const STARTER_KEYS = ['starter1', 'starter2', 'starter3'] as const;

export function OnboardingOverlay({ eligible, onTryPrompt, onClose }: Props) {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  if (!eligible || dismissed) return null;

  function persistDismiss() {
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* private mode — fine, it just may show again next session */
    }
    setDismissed(true);
  }

  function close() {
    persistDismiss();
    onClose();
  }

  function pick(prompt: string) {
    persistDismiss();
    onTryPrompt(prompt);
  }

  return (
    <div
      onClick={close}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(26, 24, 20, 0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 900,
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--paper-0)',
          border: '1px solid var(--ink-0)',
          borderRadius: 6,
          maxWidth: 540,
          width: '100%',
          padding: '32px 32px 26px',
          boxShadow: '8px 8px 0 0 rgba(26,24,20,0.12)',
        }}
      >
        <div
          className="mono"
          style={{
            fontSize: 10.5,
            letterSpacing: '.16em',
            fontWeight: 700,
            color: 'var(--amber)',
            marginBottom: 12,
          }}
        >
          {t('onboarding.kicker')}
        </div>

        <h2
          className="serif"
          style={{
            fontSize: 28,
            fontWeight: 500,
            lineHeight: 1.12,
            margin: '0 0 18px',
            color: 'var(--ink-0)',
            letterSpacing: '-.01em',
          }}
        >
          {t('onboarding.titleLine1')}<br />
          <em style={{ color: 'var(--ink-2)' }}>{t('onboarding.titleLine2')}</em>
        </h2>

        {/* 3-beat loop */}
        <ol
          style={{
            listStyle: 'none',
            padding: 0,
            margin: '0 0 24px',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <Beat n="1" title={t('onboarding.beat1Title')}>
            {t('onboarding.beat1Body')}
          </Beat>
          <Beat n="2" title={t('onboarding.beat2Title')}>
            {t('onboarding.beat2Body')}
          </Beat>
          <Beat n="3" title={t('onboarding.beat3Title')}>
            {t('onboarding.beat3Body')}
          </Beat>
        </ol>

        {/* Starter prompts */}
        <div
          className="mono"
          style={{
            fontSize: 10,
            letterSpacing: '.14em',
            color: 'var(--ink-3)',
            fontWeight: 700,
            marginBottom: 10,
          }}
        >
          {t('onboarding.tryOne')}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 22 }}>
          {STARTER_KEYS.map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => pick(t(`onboarding.${k}Prompt`))}
              style={{
                textAlign: 'left',
                padding: '11px 14px',
                border: '1px solid var(--rule-s)',
                background: 'var(--paper-1)',
                color: 'var(--ink-0)',
                cursor: 'pointer',
                fontSize: 13.5,
                lineHeight: 1.4,
                transition: 'border-color .12s, background .12s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--amber)';
                e.currentTarget.style.background = 'var(--amber-soft)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--rule-s)';
                e.currentTarget.style.background = 'var(--paper-1)';
              }}
            >
              <span style={{ fontWeight: 600 }}>{t(`onboarding.${k}Label`)}</span>
              <span style={{ color: 'var(--ink-2)' }}> →</span>
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={close}
            className="mono"
            style={{
              fontSize: 11,
              letterSpacing: '.12em',
              fontWeight: 700,
              padding: '9px 14px',
              border: '1px solid var(--rule-s)',
              background: 'transparent',
              color: 'var(--ink-2)',
              cursor: 'pointer',
            }}
          >
            {t('onboarding.skip')}
          </button>
        </div>
      </div>
    </div>
  );
}

function Beat({
  n,
  title,
  children,
}: {
  n: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <li style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      <span
        className="serif"
        style={{
          fontSize: 18,
          fontStyle: 'italic',
          color: 'var(--amber)',
          lineHeight: 1.3,
          flexShrink: 0,
          width: 18,
        }}
      >
        {n}
      </span>
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink-0)', lineHeight: 1.35 }}>
          {title}
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--ink-2)', lineHeight: 1.5, marginTop: 2 }}>
          {children}
        </div>
      </div>
    </li>
  );
}
